import "./Tabs.scss"

import React, { Fragment, FunctionComponent, ReactNode, useCallback, useEffect, useState } from "react"
import cx from "classnames"

import { ITabsItemProps } from "./TabsItem/TabsItem"
import TabsItemInternal from "./TabsItemInternal/TabsItemInternal"

interface ITabsProps {
    defaultActiveIndex?: number | null
    defaultActiveSubIndex?: number | null
    defaultActiveId?: string
    onSwitchTab?(index: number, subIndex?: number, id?: string): void
    defineManualSwitchTab?: (func: (index: number, subIndex?: number, id?: string) => void) => void
    className?: string
    disabled?: boolean
    toolbar?: ReactNode
    tabsItemsClass?: string
}

const Tabs: FunctionComponent<React.PropsWithChildren<ITabsProps>> = ({
    defaultActiveIndex = 0,
    defaultActiveSubIndex = null,
    defaultActiveId,
    onSwitchTab,
    defineManualSwitchTab,
    className,
    children,
    disabled,
    toolbar,
    tabsItemsClass,
    ...others
}) => {
    const [activeId, setActiveId] = useState(defaultActiveId)
    const [activeIndex, setActiveIndex] = useState(getDefaultActiveIndex())
    const [activeSubIndex, setActiveSubIndex] = useState(defaultActiveSubIndex)

    function getDefaultActiveIndex() {
        if (!defaultActiveId) {
            return defaultActiveIndex
        }

        let resultIndex = 0
        React.Children.forEach(children, (c, index) => {
            if (!c) return
            const child = c as React.ReactElement<any, React.JSXElementConstructor<ITabsItemProps>>
            if (child.props.id === defaultActiveId) {
                resultIndex = index
            }
        })
        return resultIndex
    }

    const handleClick = useCallback((index: number, subIndex?: number, id?: string) => {
        setActiveIndex(index)
        setActiveSubIndex(subIndex ?? null)
        setActiveId(id)
        if (typeof onSwitchTab !== "function") return
        onSwitchTab(index, subIndex, id)
    }, [onSwitchTab])

    useEffect(() => {
        if (!defineManualSwitchTab) return
        defineManualSwitchTab(handleClick)
    }, [defineManualSwitchTab, handleClick])

    const tabs = React.Children.map(children, (c, index) => {
        if (!c) return null

        const child = c as React.ReactElement<any, React.JSXElementConstructor<ITabsItemProps>>

        return (
            <li key={index} className="tabs__item">
                <TabsItemInternal {...child.props}
                    index={index}
                    activeId={activeId}
                    activeIndex={activeIndex}
                    activeSubIndex={activeSubIndex}
                    disabled={disabled}
                    onClick={handleClick} />
            </li>
        )
    })

    const content = React.Children.map(children, (child, index) => {
        if (!child) return null
        if (typeof activeSubIndex !== "number") {
            if ((child as React.ReactElement<any>).props.children) {
                return <div key={index} className={activeIndex === index ? 'd-block' : 'd-none'}>
                    {(child as React.ReactElement<any>).props.children}
                </div>
            } else {
                return null
            }
        }
        if (activeIndex === index) {
            if ((child as React.ReactElement<any>)?.props?.children instanceof Array && (child as React.ReactElement<any>)?.props?.children?.[activeSubIndex]?.props?.children) {
                return <Fragment key={index}>
                    {(child as React.ReactElement<any>).props.children[activeSubIndex].props.children}
                </Fragment>
            } else {
                return <Fragment key={index}>
                    {(child as React.ReactElement<any>).props.children.props.children}
                </Fragment>
            }
        }
    })

    return (
        <div className={cx(["tabs", className])} {...others}>
            <div className="tabs__header">
                <ul className={cx("tabs__items", tabsItemsClass)}>
                    {tabs}
                </ul>
                {!!toolbar &&
                    <div className="tabs__toolbar">
                        {toolbar}
                    </div>
                }
            </div>
            {!!content && !!content.length &&
                <div className="tabs__content">
                    {content}
                </div>
            }
        </div>
    )
}

export default Tabs
